import { useTranslation } from 'react-i18next';

import { SpinnerIconSVG } from 'src/assets/svg';
import { DocumentsIcon } from 'src/components/Icons/icons';
import {
    OrganizationModuleType,
    canEmployeeReadOrganizationModule,
    canManageAppContent,
    isOrganizationModuleEnabled,
} from 'src/domain/organization';
import {
    UserRole,
    getPrimaryRole,
    isOrganizationAdministrator,
} from 'src/domain/user';
import { useCurrentOrganization } from 'src/view/hooks/useCurrentOrganization';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { OrganizationDocumentsSection } from '../Admin/components/OrganizationDocumentsSection';
import { ModulePageLayout } from '../components/ModulePageLayout';
import { ModuleUnavailableState } from '../components/ModuleUnavailableState';
import { AppContentDocumentCards } from './AppContentDocumentCards';

export const AppContent = () => {
    const { t } = useTranslation();
    const user = useSignedInUser();
    const role = getPrimaryRole(user);
    const { organization } = useCurrentOrganization();

    if (!organization) {
        return (
            <div className="grid justify-center">
                <SpinnerIconSVG className="h-8 w-8 animate-spin text-woodyBrown" />
            </div>
        );
    }

    const moduleName = t('modules.appContent.navLabel');
    const isOrganizationAdmin = isOrganizationAdministrator(user);

    if (
        !isOrganizationModuleEnabled(
            organization,
            OrganizationModuleType.appContent,
        )
    ) {
        return (
            <ModuleUnavailableState
                icon={DocumentsIcon}
                moduleName={moduleName}
                reason="notEnabled"
            />
        );
    }

    const hasReadAccess =
        isOrganizationAdmin ||
        role !== UserRole.creator ||
        canEmployeeReadOrganizationModule(
            organization,
            OrganizationModuleType.appContent,
            user?.id,
        );

    if (!hasReadAccess) {
        return (
            <ModuleUnavailableState
                icon={DocumentsIcon}
                moduleName={moduleName}
                reason="accessRequired"
            />
        );
    }

    return (
        <ModulePageLayout
            basePath="/app/documents"
            canManageSettings={!user?.supportMode && isOrganizationAdmin}
        >
            {!user?.supportMode &&
            canManageAppContent(organization, user?.id) ? (
                <div className="h-full overflow-y-auto pb-8">
                    <div className="rounded-2xl bg-white bg-opacity-50 p-5 text-woodyBrown sm:p-6">
                        <OrganizationDocumentsSection
                            organization={organization}
                        />
                    </div>
                </div>
            ) : (
                <AppContentDocumentCards organization={organization} />
            )}
        </ModulePageLayout>
    );
};
